/**
 * Taxonomy normalization functions.
 * Maps raw tool categories and algorithm types from TOOL_SPECs into canonical enums.
 */

import type { CanonicalToolCategory, ToolFlags } from "./types";

/**
 * Normalizes a raw tool category string into a canonical category and flags.
 * 
 * @param rawCategory - The category from TOOL_SPEC.identity.category
 * @param toolName - The tool name for disambiguation
 * @returns Normalized category and flags
 */
export function normalizeToolCategory(
  rawCategory: string,
  toolName: string
): {
  category: CanonicalToolCategory;
  flags: ToolFlags;
} {
  const normalizedName = toolName.toLowerCase();
  const normalizedCategory = rawCategory.toLowerCase();

  // Map scoring models
  if (
    normalizedCategory === "scoring_model" ||
    normalizedName.includes("rice") ||
    normalizedName.includes("wsjf") ||
    normalizedName.includes("cost-of-delay") ||
    normalizedName.includes("cost_of_delay") ||
    normalizedName.includes("weighted") ||
    normalizedName.includes("pareto")
  ) {
    const flags: ToolFlags = {
      isArrangeCapable: true,
    };

    // Special flags for specific tools
    if (normalizedName.includes("pareto")) {
      flags.isSelectionModel = true;
    }

    return {
      category: "scoring_model",
      flags,
    };
  }

  // Map categorization models
  if (
    normalizedCategory === "categorization_model" ||
    normalizedName.includes("moscow") ||
    normalizedName.includes("eisenhower") ||
    normalizedName.includes("impact-effort") ||
    normalizedName.includes("impact_effort") ||
    normalizedName.includes("pyramid") ||
    normalizedName.includes("matrix")
  ) {
    return {
      category: "categorization_model",
      flags: {
        isArrangeCapable: true,
      },
    };
  }

  // Map comparison models
  if (
    normalizedCategory === "comparison_model" ||
    normalizedCategory === "multi_criteria_decision_making" ||
    normalizedName.includes("pairwise") ||
    normalizedName.includes("ahp") ||
    normalizedName.includes("comparison")
  ) {
    return {
      category: "comparison_model",
      flags: {
        isArrangeCapable: true,
      },
    };
  }

  // Map voting models
  if (
    normalizedCategory === "voting_model" ||
    normalizedName.includes("dot") ||
    normalizedName.includes("voting") ||
    normalizedName.includes("stack") ||
    normalizedName.includes("buy-a-feature") ||
    normalizedName.includes("buy_a_feature")
  ) {
    const flags: ToolFlags = {
      isArrangeCapable: true,
    };

    // Special flags for simulation games
    if (
      normalizedName.includes("buy-a-feature") ||
      normalizedName.includes("buy_a_feature")
    ) {
      flags.isSimulationGame = true;
    }

    return {
      category: "voting_model",
      flags,
    };
  }

  // Handle decision frameworks (not arrange-capable)
  if (
    normalizedName.includes("fast-vs-right") ||
    normalizedName.includes("fast_vs_right") ||
    normalizedCategory === "decision_framework"
  ) {
    return {
      category: "scoring_model", // Default fallback category
      flags: {
        isDecisionFramework: true,
        isArrangeCapable: false,
      },
    };
  }

  // Default fallback: treat as scoring model
  return {
    category: "scoring_model",
    flags: {
      isArrangeCapable: true,
    },
  };
}

/**
 * Normalizes algorithm score_model_type into a canonical string.
 * This is stored in annotations for reference but not exposed as a first-class field.
 * 
 * @param rawType - The score_model_type from TOOL_SPEC.algorithm.score_model_type
 * @returns Normalized algorithm type
 */
export function normalizeAlgorithmType(rawType: string): string {
  const normalized = rawType.toLowerCase();

  if (
    normalized === "formula" ||
    normalized === "weighted_sum" ||
    normalized === "ratio" ||
    normalized.includes("formula")
  ) {
    return "formula";
  }

  if (
    normalized === "categorization" ||
    normalized === "quadrant_categorization" ||
    normalized.includes("categorization")
  ) {
    return "categorization";
  }

  if (
    normalized === "comparison" ||
    normalized === "comparison_based" ||
    normalized.includes("comparison")
  ) {
    return "comparison";
  }

  if (
    normalized === "vote_count" ||
    normalized === "voting" ||
    normalized.includes("vote")
  ) {
    return "vote_count";
  }

  // Default fallback
  return normalized;
}


